import { getState, type WidgetContext } from "./state";

type IframeResult = {
    iframe: HTMLIFrameElement | null;
    reused: boolean;
};

/**
 * Get existing iframe if origin matches, or create a new one (per instance).
 * Reused = same origin -> update via postMessage. Not reused = new iframe.
 */
export const getOrCreateIframe = (
    widgetUrl: string,
    ctx: WidgetContext,
): IframeResult => {
    const newOrigin = new URL(widgetUrl).origin;
    const state = getState(ctx);
    const iframeId = ctx.ids.iframe;

    // Check for existing iframe
    const existingEl = document.getElementById(iframeId) as HTMLIFrameElement | null;
    if (existingEl && state.iframe) {
        try {
            const existingOrigin = new URL(existingEl.src).origin;
            if (existingOrigin === newOrigin) {
                return { iframe: existingEl, reused: true };
            }
        } catch {
            // Invalid src — fall through to create new
        }
        // Different origin — remove old iframe
        existingEl.remove();
    }

    // Create new hidden iframe
    const iframe = document.createElement("iframe");
    iframe.id = iframeId;
    iframe.src = widgetUrl;
    iframe.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100dvw;
        height: 100dvh;
        border: none;
        z-index: -1;
        visibility: hidden;
        pointer-events: none;
        opacity: 0;
    `;
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");
    document.body.appendChild(iframe);

    return { iframe, reused: false };
};

/**
 * Remove this instance's iframe + related DOM elements (placeholder, skeleton).
 * Used by destroyClient. Scoped to the instance's namespaced ids.
 */
export const removeExistingIframes = (ctx: WidgetContext): void => {
    const ids = [ctx.ids.iframe, ctx.ids.placeholder, ctx.ids.skeleton];
    for (const id of ids) {
        const el = document.getElementById(id);
        if (el) el.remove();
    }
};
